"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { UserCheck, Loader2 } from "lucide-react"
import { LiquidGlassNotification, useLiquidNotification } from "@/components/ui/liquid-glass-notification"

interface RegistrationFormProps {
  eventId: string
  eventTitle?: string
  eventPrice?: number | null
  isFree?: boolean
  registrationType?: "paid" | "free" | "interest"
}

export function RegistrationForm({ eventId, eventTitle, eventPrice, isFree, registrationType = "paid" }: RegistrationFormProps) {
  const { toast } = useToast()
  const { notification, showNotification, hideNotification } = useLiquidNotification()
  const [isLoading, setIsLoading] = useState(false)
  const [isRegistered, setIsRegistered] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    position: "",
    phone: "",
    special_requirements: "",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await fetch("/api/registrations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          ...formData, 
          event_id: eventId,
          event_title: eventTitle,
          event_price: eventPrice,
          is_free: isFree
        }),
      })

      if (response.ok) {
        setIsRegistered(true)
        const successMessage = registrationType === "interest" 
          ? "Interest registered successfully! We'll contact you with more details."
          : "Confirmation email and ticket are being sent to your inbox."
        toast({
          title: registrationType === "interest" ? "Interest Registered!" : "Registration Successful!",
          description: successMessage,
        })
        setFormData({
          name: "",
          email: "",
          company: "",
          position: "",
          phone: "",
          special_requirements: "",
        })
      } else {
        const errorData = await response.json()
        console.error('Registration error:', errorData)
        showNotification(errorData.error || "Registration failed", "error")
        throw new Error(errorData.error || "Registration failed")
      }
    } catch (error: any) {
      if (!error.message.includes("already registered")) {
        showNotification(
          error.message || "Please try again or contact us for assistance.",
          "error"
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  if (isRegistered) {
    return (
      <div className="text-center py-8 space-y-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <UserCheck className="h-8 w-8 text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-green-800">
            {registrationType === "interest" ? "Interest Registered!" : "Registration Confirmed!"}
          </h3>
          {registrationType === "interest" ? (
            <p className="text-sm text-green-600 mt-2">
              Thank you for your interest! We'll be in touch with more details about this event.
            </p>
          ) : (
            <>
              <p className="text-sm text-green-600 mt-2">
                Thank you for registering! You'll receive two emails:
              </p>
              <ul className="text-sm text-green-600 mt-2 list-disc list-inside">
                <li>A confirmation email with event details</li>
                <li>A separate email with your PDF ticket</li>
              </ul>
              <p className="text-xs text-green-600 mt-2">
                Please check your spam folder if you don't see them within a few minutes.
              </p>
            </>
          )}
        </div>
        <Button 
          variant="outline" 
          onClick={() => setIsRegistered(false)}
          className="mt-4"
        >
          {registrationType === "interest" ? "Register Another Person" : "Register Another Person"}
        </Button>
      </div>
    )
  }

  return (
    <>
      {notification && (
        <LiquidGlassNotification
          message={notification.message}
          variant={notification.variant}
          onClose={hideNotification}
        />
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Full Name *</Label>
        <Input 
          id="name" 
          value={formData.name} 
          onChange={(e) => handleChange("name", e.target.value)} 
          placeholder="John Doe"
          required 
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email Address *</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          placeholder="john@company.com"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="company">Company</Label>
        <Input 
          id="company" 
          value={formData.company} 
          onChange={(e) => handleChange("company", e.target.value)} 
          placeholder="Acme Corporation"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="position">Position/Title</Label>
        <Input 
          id="position" 
          value={formData.position} 
          onChange={(e) => handleChange("position", e.target.value)} 
          placeholder="CEO, Director, Manager"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number</Label>
        <Input 
          id="phone" 
          type="tel" 
          value={formData.phone} 
          onChange={(e) => handleChange("phone", e.target.value)} 
          placeholder="+1 (555) 123-4567"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="special_requirements">Special Requirements</Label>
        <Textarea
          id="special_requirements"
          value={formData.special_requirements}
          onChange={(e) => handleChange("special_requirements", e.target.value)}
          placeholder="Dietary restrictions, accessibility needs, etc."
          rows={3}
        />
      </div>

      <div className="pt-4 border-t">
        {registrationType !== "interest" && (
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium">Registration Fee:</span>
            <span className="text-lg font-bold text-primary">
              {isFree ? "Free" : eventPrice ? `$${eventPrice.toLocaleString()}` : "TBA"}
            </span>
          </div>
        )}
        
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {registrationType === "interest" ? "Submitting..." : "Registering..."}
            </>
          ) : (
            registrationType === "interest" 
              ? "Register Interest" 
              : `Register ${isFree ? "for Free" : "Now"}`
          )}
        </Button>
        
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {registrationType === "interest"
            ? "No commitment required. We'll contact you with event details."
            : isFree 
              ? "No payment required. You'll receive a confirmation email."
              : "You'll receive payment instructions via email after registration."
          }
        </p>
      </div>
    </form>
    </>
  )
}
